import { c as _c } from "react/compiler-runtime";
import { IcoArrowDown16, IcoArrowUp16, IcoPlusSmall16, IcoTrash16, IcoWarning16 } from '@onefootprint/icons';
import type { LanguageDefinitionResponse, NodeExecutionDetailRules, PublicTraceEntry, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import { Form, IconButton, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpressionEditor } from '@/components/expression-editor';
import { getFiredRuleId } from '@/components/playbook-builder/utils/get-fired-rule-id';
import { getTraceHighlightStyles } from '@/components/playbook-builder/utils/get-trace-highlight-styles';
import CustomBadge from '../../conditional-node/components/custom-badge';
import type { RulesFormData } from '../rules-node.types';
type RulesGroupProps = {
  groupIndex: number;
  canEdit: boolean;
  autocompleteItems: TemplateAutocomplete;
  languageDefinition?: LanguageDefinitionResponse;
  onRemoveGroup: () => void;
  numGroups: number;
  onMoveGroup: (oldIndex: number, newIndex: number) => void;
  trace?: PublicTraceEntry;
  warningMessages?: Map<string, string>;
};
export const newCondition = () => {
  const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);
  return {
    expression: '',
    id: `rule_${generateId()}`
  };
};
export const RulesGroup = t0 => {
  const $ = _c(102);
  const {
    groupIndex,
    canEdit,
    autocompleteItems,
    languageDefinition,
    onRemoveGroup,
    numGroups,
    onMoveGroup,
    trace,
    warningMessages
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rulesDataNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "rules"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tRules
  } = useTranslation("playbook-builder", t2);
  const {
    control
  } = useFormContext();
  const t3 = `groups.${groupIndex}.conditions`;
  let t4;
  if ($[2] !== control || $[3] !== t3) {
    t4 = {
      control,
      name: t3,
      keyName: "_fieldId"
    };
    $[2] = control;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    fields: groupConditions,
    append: appendCondition,
    remove: removeCondition
  } = useFieldArray(t4);
  const nodeExecDetail = trace?.context as NodeExecutionDetailRules | undefined;
  let t5;
  if ($[5] !== nodeExecDetail) {
    t5 = nodeExecDetail?.kind === "rules" ? getFiredRuleId(nodeExecDetail.groupResults) : undefined;
    $[5] = nodeExecDetail;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const firedRuleId = t5;
  let handleMoveGroupDown;
  let handleMoveGroupUp;
  let hasNextGroup;
  let hasPreviousGroup;
  let isOnlyGroup;
  let onAddCondition;
  let t6;
  let wasConditionTriggered;
  if ($[7] !== appendCondition || $[8] !== firedRuleId || $[9] !== groupConditions || $[10] !== groupIndex || $[11] !== nodeExecDetail || $[12] !== numGroups || $[13] !== onMoveGroup) {
    let t7;
    if ($[22] !== firedRuleId) {
      t7 = t8 => {
        const {
          id
        } = t8;
        return id === firedRuleId;
      };
      $[22] = firedRuleId;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    const wasGroupExecuted = groupConditions.some(t7);
    const borderClass = getTraceHighlightStyles(wasGroupExecuted);
    let t8;
    if ($[24] !== appendCondition) {
      t8 = () => {
        appendCondition(newCondition());
      };
      $[24] = appendCondition;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    onAddCondition = t8;
    isOnlyGroup = numGroups === 1;
    hasNextGroup = groupIndex < numGroups - 1;
    hasPreviousGroup = groupIndex > 0;
    let t9;
    if ($[26] !== groupIndex || $[27] !== onMoveGroup) {
      t9 = () => onMoveGroup(groupIndex, groupIndex - 1);
      $[26] = groupIndex;
      $[27] = onMoveGroup;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    handleMoveGroupUp = t9;
    let t10;
    if ($[29] !== groupIndex || $[30] !== onMoveGroup) {
      t10 = () => onMoveGroup(groupIndex, groupIndex + 1);
      $[29] = groupIndex;
      $[30] = onMoveGroup;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    handleMoveGroupDown = t10;
    wasConditionTriggered = conditionId => {
      if (!nodeExecDetail || !wasGroupExecuted) {
        return false;
      }
      return nodeExecDetail.groupResults.find(r => r.ruleId === conditionId)?.result === true;
    };
    t6 = cx("rounded flex flex-col gap-3", borderClass);
    $[7] = appendCondition;
    $[8] = firedRuleId;
    $[9] = groupConditions;
    $[10] = groupIndex;
    $[11] = nodeExecDetail;
    $[12] = numGroups;
    $[13] = onMoveGroup;
    $[14] = handleMoveGroupDown;
    $[15] = handleMoveGroupUp;
    $[16] = hasNextGroup;
    $[17] = hasPreviousGroup;
    $[18] = isOnlyGroup;
    $[19] = onAddCondition;
    $[20] = t6;
    $[21] = wasConditionTriggered;
  } else {
    handleMoveGroupDown = $[14];
    handleMoveGroupUp = $[15];
    hasNextGroup = $[16];
    hasPreviousGroup = $[17];
    isOnlyGroup = $[18];
    onAddCondition = $[19];
    t6 = $[20];
    wasConditionTriggered = $[21];
  }
  let t7;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-body-2 text-tertiary flex flex-row gap-1 whitespace-nowrap">Set value to</span>;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const t8 = `groups.${groupIndex}.value`;
  let t9;
  if ($[33] !== t) {
    t9 = t(_temp);
    $[33] = t;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t) {
    t10 = t(_temp2);
    $[35] = t;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = {
      required: t10
    };
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t11 || $[40] !== t8 || $[41] !== t9) {
    t12 = <div className="flex flex-row items-center gap-2 w-full min-h-[32px]">{t7}<Form.Input name={t8} placeholder={t9} size="compact" rules={t11} className="max-w-[200px]" /></div>;
    $[39] = t11;
    $[40] = t8;
    $[41] = t9;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== canEdit || $[44] !== handleMoveGroupUp || $[45] !== hasPreviousGroup || $[46] !== t) {
    t13 = hasPreviousGroup && <IconButton aria-label={t(_temp3)} onClick={handleMoveGroupUp} icon={IcoArrowUp16} size="tiny" disabled={!canEdit} />;
    $[43] = canEdit;
    $[44] = handleMoveGroupUp;
    $[45] = hasPreviousGroup;
    $[46] = t;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== canEdit || $[49] !== handleMoveGroupDown || $[50] !== hasNextGroup || $[51] !== t) {
    t14 = hasNextGroup && <IconButton aria-label={t(_temp4)} onClick={handleMoveGroupDown} icon={IcoArrowDown16} size="tiny" disabled={!canEdit} />;
    $[48] = canEdit;
    $[49] = handleMoveGroupDown;
    $[50] = hasNextGroup;
    $[51] = t;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== canEdit || $[54] !== isOnlyGroup || $[55] !== onRemoveGroup || $[56] !== t) {
    t15 = !isOnlyGroup && <IconButton aria-label={t(_temp5)} onClick={onRemoveGroup} icon={IcoTrash16} size="tiny" disabled={!canEdit} destructive={true} />;
    $[53] = canEdit;
    $[54] = isOnlyGroup;
    $[55] = onRemoveGroup;
    $[56] = t;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== t12 || $[59] !== t13 || $[60] !== t14 || $[61] !== t15) {
    t16 = <div className="flex flex-row bg-secondary border-b border-b-tertiary p-2 items-center gap-3">{t12}{t13}{t14}{t15}</div>;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] !== autocompleteItems || $[64] !== canEdit || $[65] !== control || $[66] !== groupConditions || $[67] !== groupIndex || $[68] !== languageDefinition || $[69] !== removeCondition || $[70] !== t || $[71] !== tRules || $[72] !== warningMessages || $[73] !== wasConditionTriggered) {
    let t18;
    if ($[75] !== autocompleteItems || $[76] !== canEdit || $[77] !== control || $[78] !== groupConditions.length || $[79] !== groupIndex || $[80] !== languageDefinition || $[81] !== removeCondition || $[82] !== t || $[83] !== tRules || $[84] !== warningMessages || $[85] !== wasConditionTriggered) {
      t18 = (condition, conditionIndex) => {
        const wasTriggered = wasConditionTriggered(condition.id);
        const warningMessage = warningMessages?.get(condition.id);
        return <div key={condition._fieldId} className="flex items-start gap-2"><span className="text-caption-1 text-tertiary min-w-[20px] pt-2">{conditionIndex === 0 ? t(_temp6) : t(_temp7)}</span><Controller name={`groups.${groupIndex}.conditions.${conditionIndex}.expression`} control={control} rules={{
            required: t(_temp8)
          }} render={t19 => {
            const {
              field,
              fieldState
            } = t19;
            return <div className="flex flex-col w-full relative"><div className="relative"><ExpressionEditor {...field} variant="input" hasError={!!fieldState.error} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} disabled={!canEdit || field.disabled} placeholder={t(_temp9)} className={wasTriggered ? "border-accent! border-2!" : undefined} />{wasTriggered && <CustomBadge>{tRules(_temp10)}</CustomBadge>}</div>{fieldState.error?.message && <span className="text-error text-caption-2 mt-2">{fieldState.error.message}</span>}{warningMessage && !fieldState.error?.message && <span className="text-warning text-caption-2 mt-2 flex items-center gap-1.5"><IcoWarning16 color="warning" className="shrink-0 size-3" />{warningMessage}</span>}</div>;
          }} /><IconButton type="button" onClick={() => removeCondition(conditionIndex)} icon={IcoTrash16} disabled={groupConditions.length === 1 || !canEdit} destructive={true} aria-label={t(_temp11)} /></div>;
      };
      $[75] = autocompleteItems;
      $[76] = canEdit;
      $[77] = control;
      $[78] = groupConditions.length;
      $[79] = groupIndex;
      $[80] = languageDefinition;
      $[81] = removeCondition;
      $[82] = t;
      $[83] = tRules;
      $[84] = warningMessages;
      $[85] = wasConditionTriggered;
      $[86] = t18;
    } else {
      t18 = $[86];
    }
    t17 = groupConditions.map(t18);
    $[63] = autocompleteItems;
    $[64] = canEdit;
    $[65] = control;
    $[66] = groupConditions;
    $[67] = groupIndex;
    $[68] = languageDefinition;
    $[69] = removeCondition;
    $[70] = t;
    $[71] = tRules;
    $[72] = warningMessages;
    $[73] = wasConditionTriggered;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  let t18;
  if ($[87] !== t17) {
    t18 = <div className="flex flex-col gap-2">{t17}</div>;
    $[87] = t17;
    $[88] = t18;
  } else {
    t18 = $[88];
  }
  const t19 = !canEdit;
  let t20;
  if ($[89] !== t) {
    t20 = t(_temp12);
    $[89] = t;
    $[90] = t20;
  } else {
    t20 = $[90];
  }
  let t21;
  if ($[91] !== onAddCondition || $[92] !== t19 || $[93] !== t20) {
    t21 = <LinkButton type="button" onClick={onAddCondition} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={t19}>{t20}</LinkButton>;
    $[91] = onAddCondition;
    $[92] = t19;
    $[93] = t20;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] !== t18 || $[96] !== t21) {
    t22 = <div className="flex flex-col gap-3 px-3 pb-3">{t18}{t21}</div>;
    $[95] = t18;
    $[96] = t21;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  let t23;
  if ($[98] !== t16 || $[99] !== t22 || $[100] !== t6) {
    t23 = <div className={t6}>{t16}{t22}</div>;
    $[98] = t16;
    $[99] = t22;
    $[100] = t6;
    $[101] = t23;
  } else {
    t23 = $[101];
  }
  return t23;
};
function _temp($) {
  return $.valuePlaceholder;
}
function _temp2($_0) {
  return $_0.valueRequired;
}
function _temp3($_1) {
  return $_1.moveGroupUp;
}
function _temp4($_2) {
  return $_2.moveGroupDown;
}
function _temp5($_3) {
  return $_3.deleteGroup;
}
function _temp6($_4) {
  return $_4.if;
}
function _temp7($_5) {
  return $_5.or;
}
function _temp8($_6) {
  return $_6.conditionRequired;
}
function _temp9($_7) {
  return $_7.conditionPlaceholder;
}
function _temp10($_8) {
  return $_8.triggered;
}
function _temp11($_9) {
  return $_9.removeCondition;
}
function _temp12($_10) {
  return $_10.addCondition;
}